import React, { useMemo } from 'react';
import { useApiQuery } from '../hooks/useApiQuery';
import api from '../services/api';

const MySessions = () => {
  // ✅ React Query: Fetch my sessions
  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
  } = useApiQuery(
    ['my-sessions'],
    () => api.get('/sessions/my').then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds
      onError: (err) => {
        console.error(err);
      },
    }
  );

  // ✅ Memoized: Format sessions for display (optional)
  const formattedSessions = useMemo(() => {
    return sessions.map(session => ({
      ...session,
      eventName: session.eventId?.name || session.eventId,
      entryTimeFormatted: new Date(session.entryTime).toLocaleString(),
      exitTimeFormatted: session.exitTime ? new Date(session.exitTime).toLocaleString() : '—',
      deviceName: session.entryDevice?.name || session.entryDevice,
    }));
  }, [sessions]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading your sessions...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error?.response?.data?.message || 'Failed to load sessions'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">My Session History</h1>
      
      <div className="bg-white rounded-lg shadow-sm border border-[#dae1e7] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {formattedSessions.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    No sessions found.
                  </td>
                </tr>
              ) : (
                formattedSessions.map((session) => (
                  <tr key={session._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.eventName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.entryTimeFormatted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.exitTimeFormatted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.deviceName}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session count */}
      <div className="mt-4 text-sm text-gray-500">
        Total sessions: <span className="font-medium text-gray-700">{formattedSessions.length}</span>
      </div>
    </div>
  );
};

export default MySessions;